import React from "react";
import { DateTime } from "luxon";
import { etapesMiseEnService, miseEnServicePossible } from "./libellesOperateur";

// ============================================================
// Encart « Mise en service » de la fiche opérateur (maquette 10) : tant qu'il
// n'est pas en service, un opérateur d'accompagnement ne reçoit aucune
// orientation. L'encart dit ce qui reste à faire, et pourquoi le bouton est
// indisponible quand il l'est (un opérateur sans site ou sans administrateur
// activé ne peut pas être mis en service ; `comptes` : les comptes de la
// structure, CE-1407). Pour un opérateur classique ou inactif, il dit
// simplement l'état. En `lectureSeule` (vue de l'opérateur connecté), les
// étapes se lisent sans bouton : la mise en service est le geste de la Caf.
// `ongletsVisibles` : les onglets de la fiche ouverts à l'utilisateur ; une
// étape ne propose pas de raccourci vers un onglet qu'il ne voit pas.
// ============================================================

const ICONES_ETAPE = {
  ok: "fa-check-circle",
  ko: "fa-times-circle",
  partiel: "fa-exclamation-circle",
  avenir: "fa-clock",
};
const LIBELLES_ETAPE = {
  ok: "Fait",
  ko: "À faire",
  partiel: "À surveiller",
  avenir: "À venir",
};

const dateFr = (iso) => (iso ? DateTime.fromISO(iso).setLocale("fr").toFormat("d MMMM yyyy") : "");

const EncartMiseEnService = ({
  fiche,
  comptes = null,
  onMettreEnService,
  onVoirOnglet,
  ongletsVisibles = null,
  enCours,
  refus,
  lectureSeule = false,
}) => {
  const raccourciPossible = (action) => !lectureSeule && (!ongletsVisibles || ongletsVisibles.includes(action.onglet));
  if (fiche.statut === "INACTIF") {
    return (
      <aside className="encart-service encart-service--muted" aria-label="Mise en service">
        <p className="encart-service__libelle">Mise en service</p>
        <p className="encart-service__valeur">Inactif</p>
        {fiche.motifInactivite && <p className="encart-service__aide">{fiche.motifInactivite}</p>}
        <p className="encart-service__aide">
          Ne reçoit plus d'orientation ; ses sites ne sont plus proposés dans les contrats
          d'engagement{(fiche.regimes || []).length ? " et les comptes de ses utilisateurs sont désactivés" : ""}.
        </p>
      </aside>
    );
  }

  if (!((fiche.regimes || []).length)) {
    return (
      <aside className="encart-service encart-service--muted" aria-label="Mise en service">
        <p className="encart-service__libelle">Opérateur classique</p>
        <p className="encart-service__valeur">Aucune mise en service requise</p>
        <p className="encart-service__aide">
          Ses sites sont proposés dans les contrats d'engagement. Cochez un régime pour en faire
          un opérateur d'accompagnement.
        </p>
      </aside>
    );
  }

  if (fiche.statut === "EN_SERVICE") {
    return (
      <aside className="encart-service" aria-label="Mise en service">
        <p className="encart-service__libelle">Mise en service</p>
        <p className="encart-service__valeur">
          En service{fiche.miseEnServiceLe ? ` depuis le ${dateFr(fiche.miseEnServiceLe)}` : ""}
        </p>
        <p className="encart-service__aide">L'opérateur peut recevoir des orientations.</p>
      </aside>
    );
  }

  const etapes = etapesMiseEnService(fiche, comptes);
  const possible = miseEnServicePossible(fiche, comptes);
  const aFaire = etapes.filter((e) => e.etat === "ko").length;
  const manques = [];
  if (etapes.some((e) => e.etat === "ko" && e.texte.includes("site"))) manques.push("au moins un site d'accueil");
  if (etapes.some((e) => e.etat === "ko" && e.texte.toLowerCase().includes("administrateur"))) manques.push("un administrateur activé");

  return (
    <aside className="encart-service encart-service--warn" aria-label="Mise en service">
      <p className="encart-service__libelle">Mise en service</p>
      <p className="encart-service__valeur">
        {aFaire
          ? `${aFaire} étape${aFaire > 1 ? "s" : ""} avant la mise en service`
          : "Prêt à être mis en service"}
      </p>
      <ul className="encart-service__etapes">
        {etapes.map((etape) => (
          <li key={etape.texte} className={`etape etape--${etape.etat}`}>
            <i className={`fa ${ICONES_ETAPE[etape.etat]}`} aria-hidden="true" />
            <span>
              <span className="sr-only">{LIBELLES_ETAPE[etape.etat]} : </span>
              {etape.texte}
              {etape.action && raccourciPossible(etape.action) && (
                <>
                  {" · "}
                  <button
                    type="button"
                    className="btn-lien-encart"
                    onClick={() => onVoirOnglet(etape.action.onglet)}
                  >
                    {etape.action.libelle}
                  </button>
                </>
              )}
            </span>
          </li>
        ))}
      </ul>
      {refus && (
        <p className="encart-service__refus" role="alert">
          {refus}
        </p>
      )}
      {lectureSeule ? (
        <p className="encart-service__aide">La mise en service est faite par la Caf.</p>
      ) : (
        <>
          <button
            type="button"
            className="btn btn-primary btn-block"
            onClick={onMettreEnService}
            disabled={!possible || enCours}
            aria-describedby={possible ? undefined : "aide-mise-en-service"}
          >
            <i className="fa fa-play-circle mr-2" aria-hidden="true" />
            Mettre en service
          </button>
          {!possible && (
            <p className="encart-service__aide" id="aide-mise-en-service">
              Disponible dès que l'opérateur a {manques.join(" et ")}.
            </p>
          )}
        </>
      )}
    </aside>
  );
};

export default EncartMiseEnService;
